/**
 * Pick another modelled element by searching for it.
 *
 * A reference field names an entity KIND, so the search goes to that kind's
 * listing. Typing filters on the server rather than in the browser, because
 * the far end may be any of several hundred requirements, and a requirement
 * matches on its statement text, which only the server holds.
 *
 * The generic ElementPicker in its server-search mode (issue #49); what is left
 * here is the shape a reference field needs. The traps this component first
 * found - an uncontrolled text box, the current value always an option - are
 * carried there now.
 */

import TextField from '@mui/material/TextField'
import { findKind } from '../../schema'
import type { Schema } from '../../schema'
import ElementPicker from '../common/ElementPicker'
import type { PickerOption } from '../common/ElementPicker'
import type { EntityRow } from '../../schema'

export interface ReferencePickerProps {
  schema: Schema
  /** The entity kind the far end must be. */
  target: string
  label: string
  helperText?: string
  error?: boolean
  disabled?: boolean
  multiple?: boolean
  value: string | string[] | null
  onChange: (value: string | string[] | null) => void
}

/** The identifier as the line, and the name under it when it says more. */
function referenceOption(row: EntityRow, value: string): PickerOption {
  return { value, note: row.name && row.name !== row.id ? String(row.name) : undefined }
}

export default function ReferencePicker({
  schema,
  target,
  label,
  helperText,
  error,
  disabled,
  multiple = false,
  value,
  onChange,
}: ReferencePickerProps) {
  if (!findKind(schema, target)) {
    return (
      <TextField
        label={label}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        helperText={`No screen for '${target}'; type the qualified name`}
        disabled={disabled}
        fullWidth
        size="small"
      />
    )
  }

  const common = {
    schema,
    kind: target,
    search: 'server' as const,
    describe: referenceOption,
    label,
    helperText,
    error,
    disabled,
    monospace: true,
  }

  return multiple ? (
    <ElementPicker
      {...common}
      multiple
      value={Array.isArray(value) ? value : []}
      onChange={onChange}
    />
  ) : (
    <ElementPicker
      {...common}
      value={typeof value === 'string' && value ? value : null}
      // Cleared is null, as it always was here: callers tell "unset" from "".
      onChange={(next) => onChange(next || null)}
    />
  )
}
